import React, { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Palette, Upload, Download, Loader2, Share2 } from 'lucide-react';
import ShareModal from '@/components/ShareModal';
import ImageActions from '@/components/ImageActions';
import AIModelSelector from '@/components/AIModelSelector';
import { isAdminTester, logAdminBypass } from '@/utils/adminBypass';
import { getToastMessage, requiresUpgrade } from '@/utils/errorClassifier';

const BackgroundGenerator = () => {
  const { api, user, updateCredits } = useAuth();
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [backgroundPrompt, setBackgroundPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [currentImageUrl, setCurrentImageUrl] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  
  // AI Model selection - consistent with all other tools
  const [selectedModel, setSelectedModel] = useState('gpt');

  const presetBackgrounds = [
    'Modern minimalist office desk',
    'Cozy autumn garden with fallen leaves',
    'Clean white marble surface',
    'Rustic wooden table with plants',
    'Beach sunset with soft waves',
    'Urban coffee shop interior'
  ];

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  }, []);

  const handleFile = (file) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target.result);
      const base64 = e.target.result.split(',')[1];
      setImage(base64);
    };
    reader.readAsDataURL(file);
    setResult(null);
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleGenerate = async () => {
    if (!backgroundPrompt.trim()) {
      toast.error('Please enter a background description');
      return;
    }

    // Check admin bypass using shared utility
    const isAdmin = isAdminTester(user);
    logAdminBypass('BackgroundGenerator', 'handleGenerate', user, isAdmin);
    
    if (!isAdmin && (user?.credits || 0) < 5) {
      toast.error('Insufficient credits. Please upgrade your plan.');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/ai/generate-background', {
        image_base64: image || '',
        background_prompt: backgroundPrompt.trim(),
        auto_enhance: true  // Always auto-enhance
      });
      setResult(response.data);
      // Set current image URL for enhance/upscale
      const imgUrl = response.data.image_base64 
        ? `data:image/png;base64,${response.data.image_base64}` 
        : response.data.image_url;
      setCurrentImageUrl(imgUrl);
      
      // Only update credits if not admin
      if (!isAdmin && response.data.credits_used > 0) {
        updateCredits((user?.credits || 0) - response.data.credits_used);
      }
      toast.success(`Background generated!${isAdmin ? ' (Admin mode)' : ` ${response.data.credits_used} credits used`}`);
    } catch (error) {
      // Standardized error classification
      toast.error(getToastMessage(error, 'Background generation failed. No credits were used.'));
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!currentImageUrl) return;
    
    try {
      if (currentImageUrl.startsWith('data:')) {
        // Base64 image
        const link = document.createElement('a');
        link.href = currentImageUrl;
        link.download = `background-${Date.now()}.png`;
        link.click();
      } else {
        // URL image - fetch and download
        const response = await fetch(currentImageUrl);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `background-${Date.now()}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
      toast.success('Download started');
    } catch (error) {
      // Fallback: open in new tab
      window.open(currentImageUrl, '_blank');
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 fade-in" data-testid="background-generator-page">
      {/* Left Panel - Controls */}
      <div className="lg:col-span-4 space-y-6">
        <div>
          <h2 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Background Generator
          </h2>
          <p className="text-sm text-muted-foreground">
            Generate custom backgrounds (5 credits)
          </p>
        </div>

        {/* Optional Image Upload */}
        <div className="space-y-2">
          <Label>Reference Image (Optional)</Label>
          <div
            className={`upload-zone py-6 ${dragActive ? 'drag-active' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => document.getElementById('bg-file-input').click()}
            data-testid="upload-zone"
          >
            <input
              id="bg-file-input"
              type="file"
              accept="image/*"
              onChange={handleFileInput}
              className="hidden"
            />
            {imagePreview ? (
              <img 
                src={imagePreview} 
                alt="Preview" 
                className="max-h-24 rounded-lg object-contain"
              />
            ) : (
              <>
                <Upload className="w-6 h-6 text-muted-foreground mb-2" />
                <p className="text-xs text-muted-foreground">Upload reference (optional)</p>
              </>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="bgPrompt">Background Description</Label>
          <Input
            id="bgPrompt"
            placeholder="Describe the background you want..."
            value={backgroundPrompt}
            onChange={(e) => setBackgroundPrompt(e.target.value)}
            data-testid="background-prompt-input"
          />
        </div>

        {/* Preset Backgrounds */}
        <div className="space-y-2">
          <Label>Quick Presets</Label>
          <div className="flex flex-wrap gap-2">
            {presetBackgrounds.map((preset, index) => (
              <button
                key={index}
                onClick={() => setBackgroundPrompt(preset)}
                className="text-xs px-3 py-1.5 bg-muted rounded-full hover:bg-accent transition-colors"
                data-testid={`preset-${index}`}
              >
                {preset.split(' ').slice(0, 3).join(' ')}...
              </button>
            ))}
          </div>
        </div>

        {/* AI Model Selector - Consistent with all other tools */}
        <div className="bg-muted/30 rounded-xl p-4">
          <AIModelSelector
            selectedModel={selectedModel}
            onModelChange={setSelectedModel}
            variant="pills"
            compact={true}
          />
        </div>

        <Button 
          className="w-full" 
          onClick={handleGenerate}
          disabled={!backgroundPrompt.trim() || loading}
          data-testid="generate-bg-btn"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <Palette className="w-4 h-4 mr-2" />
              Generate Background
            </>
          )}
        </Button>

        {(imagePreview || backgroundPrompt) && (
          <Button 
            variant="outline" 
            className="w-full"
            onClick={() => {
              setImage(null);
              setImagePreview(null);
              setBackgroundPrompt('');
              setResult(null);
            }}
            data-testid="clear-all-btn"
          >
            Clear All
          </Button>
        )}
      </div>

      {/* Right Panel - Result */}
      <div className="lg:col-span-8">
        <Card className="h-full">
          <CardContent className="p-6">
            {loading ? (
              <div className="preview-canvas h-full min-h-[400px]">
                <div className="text-center">
                  <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-muted-foreground" />
                  <p className="text-muted-foreground">Generating background...</p>
                </div>
              </div>
            ) : !result ? (
              <div className="preview-canvas h-full min-h-[400px]">
                <div className="text-center">
                  <div className="w-16 h-16 bg-muted rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Palette className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <p className="text-muted-foreground">Describe a background to generate</p>
                </div>
              </div>
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="space-y-4"
              >
                <div className="relative rounded-xl overflow-hidden bg-muted/30">
                  <img 
                    src={currentImageUrl}
                    alt="Background"
                    className="w-full h-auto"
                    data-testid="background-image"
                  />
                </div>
                
                {/* Enhance & Upscale Actions */}
                <ImageActions
                  imageUrl={currentImageUrl}
                  onImageUpdate={(newUrl) => setCurrentImageUrl(newUrl)}
                  originalPrompt={backgroundPrompt}
                  imageStyle="photorealistic"
                  showDownload={false}
                />
                
                <div className="flex gap-4">
                  <Button onClick={handleDownload} className="flex-1" data-testid="download-bg-btn">
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => setShowShareModal(true)}
                    className="flex-1"
                    data-testid="share-bg-btn"
                  >
                    <Share2 className="w-4 h-4 mr-2" />
                    Share
                  </Button>
                </div>
                <Button 
                  variant="ghost"
                  onClick={() => {
                    setResult(null);
                    setCurrentImageUrl(null);
                  }}
                  className="w-full"
                >
                  Generate Another
                </Button>
              </motion.div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Share Modal */}
      {result && (
        <ShareModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          creationId={result.creation_id}
          imageBase64={result.image_base64}
          title="My Custom Background"
          type="background_generate"
        />
      )}
    </div>
  );
};

export default BackgroundGenerator;
